import React from "react";

import { NotificationTypename } from "./types";
import { PermissionedMutation } from "../permissions/mutationPermissions.generated";

const notificationImages = {
	bank: "/assets/images/notifications/icons/bank.svg",
	funds: "/assets/images/notifications/icons/funds.svg",
	entity: "/assets/images/notifications/icons/entity.svg",
	personTick: "/assets/images/notifications/icons/person-tick.svg",
	envelopeTick: "/assets/images/notifications/icons/envelope-tick.svg",
	locationMarker: "/assets/images/notifications/icons/location-marker.svg",
	personDocument: "/assets/images/notifications/icons/person-document.svg",
	identityDocument: "/assets/images/notifications/icons/identity-document.svg",
	locationDocument: "/assets/images/notifications/icons/location-document.svg",
};

export const notificationsContent: Record<
	Exclude<NotificationTypename, "AddInvestingEntityNotification">,
	{
		title: () => React.ReactNode;
		description: () => React.ReactNode;
		icon: string;
		mutationPermission?: PermissionedMutation;
	}
> = {
	VerifyAddressNotification: {
		title: () => "Verify your address",
		description: () =>
			"Financial regulations require us to verify your physical address.",
		icon: notificationImages.locationDocument,
	},
	ProvideSourceOfFundsNotification: {
		title: () => "Provide source of funds",
		description: () =>
			"Financial regulations require us to verify the source of funds for your investing entity.",
		icon: notificationImages.funds,
		mutationPermission: "provideSourceOfFunds",
	},
	ConfirmEmailAddressNotification: {
		title: () => "Confirm your email address",
		description: () =>
			"We've emailed you a confirmation link. Click the link in the email to confirm your address.",
		icon: notificationImages.envelopeTick,
	},
	VerifyIdentityNotification: {
		title: () => "Verify your identity",
		description: () =>
			"Financial regulations require us to confirm your identity.",
		icon: notificationImages.identityDocument,
	},
	VerifyBiometricIdentityNotification: {
		title: () => "Verify your identity",
		description: () =>
			"Financial regulations require us to confirm your identity.",
		icon: notificationImages.identityDocument,
	},
	ConfirmIdentityDocumentDetailsNotification: {
		title: () => "Confirm identity document details",
		description: () =>
			"We couldn't verify your identity document details electronically. Please contact us.",
		icon: notificationImages.identityDocument,
	},
	ProvideBeneficialOwnerContactDetailsNotification: {
		title: () => "Add contact details for all related parties",
		description: () =>
			"Financial regulations require us to verify the identities for all related parties of your investing entity.",
		icon: notificationImages.personDocument,
		mutationPermission: "completeBeneficialOwnerContactDetails",
	},
	ProvideCityOfBirthNotification: {
		title: () => "Provide city of birth",
		description: () =>
			"Financial regulations require us to record your city of birth.",
		icon: notificationImages.locationMarker,
	},
	ProvideInvestingEntityDocumentsNotification: {
		title: () => "Add investing entity documents",
		description: () =>
			"We couldn't verify your investing entity from the documents supplied. Please upload the requested documentation in an approved format.",
		icon: notificationImages.entity,
		mutationPermission: "uploadInvestingEntityDocuments",
	},
	ReattemptVerifyBiometricIdentityNotification: {
		title: () => "Verify your identity",
		description: () =>
			"We couldn't verify your identity from the document supplied. Please try again.",
		icon: notificationImages.identityDocument,
	},
	ReattemptVerifyAddressNotification: {
		title: () => "Verify your address",
		description: () =>
			"We couldn't verify your address electronically. Please upload a valid proof of address.",
		icon: notificationImages.locationMarker,
	},
	ReattemptProvideSourceOfFundsNotification: {
		title: () => "Provide source of funds",
		description: () =>
			"We couldn't verify your source of funds from the document supplied. Please upload the requested documentation in an approved format.",
		icon: notificationImages.funds,
		mutationPermission: "provideSourceOfFunds",
	},
	AddBankAccountNotification: {
		title: () => "Add bank account",
		description: () =>
			"We need your investing entity's bank details to pay your share of fund distributions.",
		icon: notificationImages.bank,
		mutationPermission: "createBankAccount",
	},
	VerifyBankAccountNotification: {
		title: () => "Add bank account",
		description: () =>
			"Verify your bank account so we can pay your share of fund distributions.",
		icon: notificationImages.bank,
		mutationPermission: "uploadBankAccountVerificationDocument",
	},
	RenewAccreditationNotification: {
		title: () => "Renew wholesale certification",
		description: () =>
			"Your wholesale certification has expired. Renew it to invest in new wholesale or accredited offers.",
		icon: notificationImages.personTick,
		mutationPermission: "confirmAccreditation",
	},
	ReattemptConfirmAccreditationNotification: {
		title: () => "Reattempt wholesale certification",
		description: () =>
			"Your wholesale certification wasn't accepted. Resubmit it to invest in new wholesale or accredited offers.",
		icon: notificationImages.personTick,
		mutationPermission: "confirmAccreditation",
	},
	ConfirmAccreditationNotification: {
		title: () => "Confirm wholesale investor status",
		description: () =>
			"Access opportunities that are exclusively available to wholesale investors.",
		icon: notificationImages.personTick,
		mutationPermission: "confirmAccreditation",
	},
	ConfirmUnsupportedCountryAccreditationNotification: {
		title: () => "Confirm wholesale investor status",
		description: () =>
			"Access opportunities that are exclusively available to wholesale investors.",
		icon: notificationImages.personTick,
		mutationPermission: "confirmAccreditation",
	},
};
